import { rubberbandIfOutOfBounds } from '@use-gesture/react';
import { getCurrentRef } from '@vista-digital/react-tools';
import { animate, clamp, useMotionTemplate, useMotionValue, useScroll, useTransform } from 'framer-motion';
import { getElementDimensions } from '../utils/get-element-dimensions';
import { getElementMaximumScrollPosition } from '../utils/get-element-maximum-scroll-position';
export function useScrollControls({ containerRef, transition, scrollIncrement }) {
    const { scrollX, scrollY } = useScroll({ container: containerRef });
    const scrollPosition = useTransform(() => ({ x: scrollX.get(), y: scrollY.get() }));
    const contentOffsetX = useMotionValue(0);
    const contentOffsetY = useMotionValue(0);
    const getContainerElement = () => {
        return getCurrentRef(containerRef);
    };
    const getContainerDimensions = () => {
        const container = getContainerElement();
        return getElementDimensions(container, 'content-box');
    };
    const getMaximumScrollPosition = () => {
        const container = getContainerElement();
        return getElementMaximumScrollPosition(container);
    };
    const getScrollIncrement = () => {
        const { height, width } = getContainerDimensions();
        return {
            x: width * scrollIncrement,
            y: height * scrollIncrement
        };
    };
    const dampenedOffsetX = useTransform(contentOffsetX, v => {
        if (!containerRef.current) {
            return 0;
        }
        const containerDimensions = getContainerDimensions();
        return v > 0
            ? rubberbandIfOutOfBounds(v, -containerDimensions.width, 0, 0.15)
            : rubberbandIfOutOfBounds(v, 0, containerDimensions.width, 0.15);
    });
    const dampenedOffsetY = useTransform(contentOffsetY, v => {
        if (!containerRef.current) {
            return 0;
        }
        const containerDimensions = getContainerDimensions();
        return v > 0
            ? rubberbandIfOutOfBounds(v, -containerDimensions.height, 0, 0.15)
            : rubberbandIfOutOfBounds(v, 0, containerDimensions.height, 0.15);
    });
    const contentTransform = useMotionTemplate `translateX(${dampenedOffsetX}px) translateY(${dampenedOffsetY}px)`;
    const setScrollX = (x, animated) => {
        const container = getContainerElement();
        const { x: maxScrollX } = getMaximumScrollPosition();
        const boundedX = clamp(0, maxScrollX, x);
        if (animated) {
            // Update the container scroll position while the scroll values animate.
            animate(scrollX.get(), boundedX, Object.assign(Object.assign({}, transition), { onUpdate: latestX => {
                    container.scrollLeft = latestX;
                } }));
        }
        else {
            // Directly set the scroll position.
            container.scrollLeft = boundedX;
            // Keep the motion value in sync, otherwise it lags.
            scrollX.set(boundedX, true);
        }
    };
    const setScrollY = (y, animated) => {
        const container = getContainerElement();
        const { y: maxScrollY } = getMaximumScrollPosition();
        const boundedY = clamp(0, maxScrollY, y);
        if (animated) {
            // Update the container scroll position while the scroll values animate.
            animate(scrollY.get(), boundedY, Object.assign(Object.assign({}, transition), { onUpdate: latestY => {
                    container.scrollTop = latestY;
                } }));
        }
        else {
            // Directly set the scroll position.
            container.scrollTop = boundedY;
            // Keep the motion value in sync, otherwise it lags.
            scrollY.set(boundedY, true);
        }
    };
    const scrollXBy = (delta, animated, constrain) => {
        let remainingDelta = delta;
        if (contentOffsetX.get() > 0 && delta > 0) {
            // Reduce the content offset
            const offsetReduction = Math.min(contentOffsetX.get(), delta);
            contentOffsetX.set(contentOffsetX.get() - offsetReduction);
            // Scroll by the remainder
            remainingDelta -= offsetReduction;
        }
        if (remainingDelta !== 0) {
            const { x: maxScrollX } = getMaximumScrollPosition();
            let newX = scrollX.get() + remainingDelta;
            if (constrain) {
                newX = Math.max(Math.min(newX, maxScrollX), 0);
            }
            else if (scrollX.get() <= 0 || scrollX.get() >= maxScrollX) {
                contentOffsetX.set(contentOffsetX.get() - remainingDelta);
            }
            if (animated) {
                // Prevent a small scroll increment from remaining
                if (newX > 0 && newX < Math.abs(remainingDelta) / 2) {
                    newX = 0;
                }
                else if (maxScrollX - newX < Math.abs(remainingDelta) / 2) {
                    newX = maxScrollX;
                }
            }
            setScrollX(newX, animated);
        }
    };
    const scrollYBy = (delta, animated, constrain) => {
        const { y: maxScrollY } = getMaximumScrollPosition();
        let newY = scrollY.get() + delta;
        if (constrain) {
            newY = Math.max(Math.min(newY, maxScrollY), 0);
        }
        else if (scrollY.get() <= 0 || scrollY.get() >= maxScrollY) {
            contentOffsetY.set(contentOffsetY.get() - delta);
        }
        if (animated) {
            // Prevent a small scroll increment from remaining
            if (newY > 0 && newY < Math.abs(delta) / 2) {
                newY = 0;
            }
            else if (maxScrollY - newY < Math.abs(delta) / 2) {
                newY = maxScrollY;
            }
        }
        setScrollY(newY, animated);
    };
    return {
        scrollPosition,
        setScrollX,
        setScrollY,
        scrollUp: (animated) => {
            const { y: deltaY } = getScrollIncrement();
            return scrollYBy(-deltaY, animated, true);
        },
        scrollDown: (animated) => {
            const { y: deltaY } = getScrollIncrement();
            return scrollYBy(deltaY, animated, true);
        },
        scrollLeft: (animated) => {
            const { x: deltaX } = getScrollIncrement();
            return scrollXBy(-deltaX, animated, true);
        },
        scrollRight: (animated) => {
            const { x: deltaX } = getScrollIncrement();
            return scrollXBy(deltaX, animated, true);
        },
        scrollXBy,
        scrollYBy,
        contentStyles: {
            transform: contentTransform
        },
        resetContentOffset: () => {
            animate(contentOffsetX, 0);
            animate(contentOffsetY, 0);
        }
    };
}
//# sourceMappingURL=use-scroll-controls.js.map